// import components
import MuiAutocompleteOption, {
  checkboxWidth,
  iconWidth,
  optionPadding
} from 'components/shared/Autocomplete/MuiAutocompleteOption';
import { Icon } from '@sdb/planning-ui';

// import hooks
// Imported straight from the module rather than the `hooks` barrel: that barrel
// pulls in `components/shared` and its import cycle.
import { useDispatch, useSelector } from 'hooks/reactRedux';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { usePrevious } from 'utils/reactHooks';

// import utils
import { getPlanunitCommaIds } from 'components/utils';
import { setUserPreference } from 'redux/actionCreators';
import { createFilterOptions } from 'utils/filterOptions';
import { isValidPlanUnit } from './util';

// import types
import type {
  AutocompleteInputChangeReason,
  AutocompleteRenderOptionState
} from '@mui/material/Autocomplete';
import type { HTMLAttributes, SyntheticEvent } from 'react';
import type {
  Interval,
  PlanGroup,
  PlanUnit,
  PlanUnitMix
} from 'components/types';
import type { UsePlanUnitSelectionParams } from './PlanUnitSelector.types';

/**
 * Owns which plan units are selected: the preselection rules, the user's own
 * selection, persisting it to the preferences, and reporting changes back to
 * the caller.
 */
export const usePlanUnitSelection = ({
  options,
  onSelected,
  onChange: fieldOnChange,
  defaultValue,
  tempOptions,
  selectedValues,
  doNotPersist,
  doNotPreselectOnlyOption,
  firstOptionIsDefault,
  shouldSaveCCPreferenceKey,
  lastUsedCCPreferenceKey
}: UsePlanUnitSelectionParams) => {
  'use memo';

  const dispatch = useDispatch();
  const preferences = useSelector((state) => state.preferences);

  const shouldSaveCostCenterSelection =
    preferences[
      (!doNotPersist && shouldSaveCCPreferenceKey) ||
        'shouldSaveCostCenterSelection'
    ] ?? true;
  const lastUsedCostCenterSelection =
    preferences[
      (!doNotPersist && lastUsedCCPreferenceKey) ||
        'lastUsedCostCenterSelection'
    ] || [];

  const [hasUserSelection, setHasUserSelection] = useState(false);
  // Marks the next emission as coming from a planner action, so consumers can
  // tell a real selection apart from the replay after a mount. A ref, not the
  // hasUserSelection state: the marking is per emission instead of sticky and
  // the emit effect below keeps its dependency array (no timing change for the
  // other consumers).
  const userSelectionRef = useRef(false);
  const [selectedOptionsIds, setSelectedOptionsIds] = useState<
    (string | number)[]
  >(
    tempOptions
      ? tempOptions.map((o) => o.id)
      : !doNotPersist && shouldSaveCostCenterSelection
      ? lastUsedCostCenterSelection
      : []
  );

  const getSelectedOptions = (): (PlanUnit | PlanGroup)[] => {
    if (
      options.length === 1 &&
      !('costCenters' in options[0]) &&
      !doNotPreselectOnlyOption
    ) {
      return [options[0]];
    }

    if (selectedOptionsIds.length) {
      return options.filter((o) => selectedOptionsIds.includes(o.uniqId));
    }

    if (hasUserSelection) {
      return []; // User has cleared the selection
    }

    let uniqIds: (string | number)[] = [];

    if (defaultValue && !(!doNotPersist && shouldSaveCostCenterSelection)) {
      uniqIds = defaultValue.map((o) => (o.uniqId ? o.uniqId : o.id));
    } else if (firstOptionIsDefault && !hasUserSelection) {
      uniqIds = options
        .filter((o) => !('costCenters' in o))
        .slice(0, 1)
        .map((o) => o.uniqId);
    }

    const selectedOptions = uniqIds
      .map((id) => options.filter(({ uniqId }) => uniqId === id)[0])
      .filter((v) => v);

    return selectedOptions;
  };

  const selectedOptions = getSelectedOptions();

  const lastUsedPreferenceKey =
    lastUsedCCPreferenceKey || 'lastUsedCostCenterSelection';

  // The compiler caches this on [dispatch, lastUsedPreferenceKey], so it is
  // stable across renders. exhaustive-deps is not compiler-aware and warns
  // about it being a dependency below; adding a useCallback would only
  // duplicate what the compiler already emits.
  const saveSelection = (value: (string | number)[]) => {
    dispatch(setUserPreference({ key: lastUsedPreferenceKey, value }));
  };

  const onChange = (e, value) => {
    const selectionList: PlanUnitMix[] = Array.isArray(value)
      ? value.filter((v) => v)
      : value
      ? [value]
      : [];
    const ids = selectionList.map((o) => o.uniqId);
    setSelectedOptionsIds(ids);
    if (!doNotPersist && shouldSaveCostCenterSelection) {
      saveSelection(ids);
    }
    userSelectionRef.current = true;
    setHasUserSelection(true);
    fieldOnChange && fieldOnChange(selectionList);
  };

  const prevSelectedOptions = usePrevious(selectedOptions);
  useEffect(() => {
    const existingIds =
      prevSelectedOptions && getPlanunitCommaIds(prevSelectedOptions);
    const newIds = getPlanunitCommaIds(selectedOptions);
    if ((existingIds || newIds) && newIds !== existingIds) {
      onSelected(selectedOptions as PlanUnit[], userSelectionRef.current);
    }
    userSelectionRef.current = false;
  }, [selectedOptions, prevSelectedOptions, onSelected]);

  const prevShouldSaveCostCenterSelection = usePrevious(
    shouldSaveCostCenterSelection
  );
  useEffect(() => {
    if (
      !doNotPersist &&
      prevShouldSaveCostCenterSelection !== undefined &&
      prevShouldSaveCostCenterSelection !== shouldSaveCostCenterSelection
    ) {
      saveSelection(
        shouldSaveCostCenterSelection
          ? selectedOptions.map((o) => o.uniqId)
          : []
      );
    }
  }, [
    prevShouldSaveCostCenterSelection,
    shouldSaveCostCenterSelection,
    doNotPersist,
    saveSelection,
    selectedOptions
  ]);

  useEffect(() => {
    if (selectedValues) {
      const ids = selectedValues.map((o) => o.uniqId ?? o.id);
      setSelectedOptionsIds(ids); // Overwrite internal state when parent controls
    }
  }, [selectedValues]);

  return { selectedOptions, onChange };
};

/**
 * What the multi- and the single-select branch both need from the surrounding
 * app: the translations, the option renderers, and how much room an option
 * needs next to its label.
 */
export const usePlanUnitBranch = ({
  compareInterval,
  isMulti
}: {
  compareInterval: Interval;
  isMulti?: boolean;
}) => {
  'use memo';

  const { t } = useTranslation('common');
  const { hasKidsPlan } = useSelector((state) => state.grants);

  const getOptionLabel = useGetOptionLabel();
  const filterOptions = useFilterOptions();
  const renderOption = useRenderOption({ isMulti, compareInterval });

  const extraWidth =
    (isMulti ? checkboxWidth : 0) +
    (hasKidsPlan ? iconWidth : 0) +
    optionPadding * 2;

  return {
    t,
    hasKidsPlan,
    getOptionLabel,
    filterOptions,
    renderOption,
    extraWidth
  };
};

/**
 * Owns what the user typed in the multi-select input. The criteria live in
 * session storage so the search survives a remount, the input value itself
 * does not.
 */
export const usePlanUnitSearch = () => {
  'use memo';

  const [searchCriteria, setSearchCriteria] = useSessionStorage<string>(
    'planUnitSearchCriteria',
    ''
  );
  const [inputValue, setInputValue] = useState(searchCriteria);

  const clearSearchCriteria = () => {
    setSearchCriteria('');
  };

  const onInputChange = (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (event && event.type === 'blur') {
      setInputValue('');
      setSearchCriteria('');
    } else if (reason !== 'reset') {
      setInputValue(value);
    }
  };

  return {
    inputValue: inputValue || searchCriteria,
    setSearchCriteria,
    clearSearchCriteria,
    onInputChange
  };
};

export const useFilterOptions = () => {
  const { planUnitDisplayModeArr = [] } = useSelector(
    (state) => state.preferences
  );
  const showNameShort = planUnitDisplayModeArr.includes(2);
  const showCode = planUnitDisplayModeArr.includes(3);
  const keys = ['name'];

  if (showNameShort) {
    keys.push('nameShort');
  }

  if (showCode) {
    keys.push('code');
  }

  return createFilterOptions('name', keys);
};

export const useGetOptionLabel = () => {
  const preferences = useSelector((state) => state.preferences);
  const planUnitDisplayModeArr = preferences?.planUnitDisplayModeArr ?? [];

  const showNameShort = planUnitDisplayModeArr.includes(2);
  const showCode = planUnitDisplayModeArr.includes(3);

  return (option: PlanUnitMix | string) => {
    if (typeof option === 'string') {
      return option;
    }

    let label = option.name;
    if (showNameShort && 'nameShort' in option) {
      label += ` - ${option.nameShort}`;
    }
    if (showCode && 'code' in option) {
      label += ` - ${option.code}`;
    }
    return label;
  };
};

type RenderOptionProps = {
  isMulti?: boolean;
  compareInterval?: Interval;
};

export const useRenderOption = ({
  isMulti,
  compareInterval
}: RenderOptionProps = {}) => {
  const getOptionLabel = useGetOptionLabel();
  const { hasKidsPlan } = useSelector((state) => state.grants);

  return function (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'> & { key?: string },
    option: PlanUnitMix,
    { selected, inputValue }: AutocompleteRenderOptionState
  ) {
    // @ts-expect-error: bug in MUI, "key" is provided in props: HTMLAttributes<HTMLLIElement, but not typed
    props.key = option.uniqId || option.id;
    const isInvalid =
      compareInterval &&
      !('costCenters' in option) &&
      !isValidPlanUnit(option.validity, compareInterval);
    return (
      <MuiAutocompleteOption
        {...props}
        key={props?.key}
        isMulti={isMulti}
        selected={selected}
        label={getOptionLabel(option)}
        isInvalid={isInvalid}
        inputValue={inputValue}
      >
        {option &&
          ('costCenters' in option || (hasKidsPlan && option.isKids)) && (
            <Icon
              type={'costCenters' in option ? 'folder' : 'children'}
              fontSize="small"
              className="left-icon"
            />
          )}
      </MuiAutocompleteOption>
    );
  };
};
